import { CertifiedBaseIcon } from '@tableau/icons/CertifiedBaseIcon';
import { RESERVEDDatasourceCanInfoIcon } from '@tableau/icons/RESERVEDDatasourceCanInfoIcon';
import { RESERVEDDatasourceCanWarningIcon } from '@tableau/icons/RESERVEDDatasourceCanWarningIcon';
import { ActivitySpinnerSize, ActivitySpinnerWidget } from '@tableau/widgets';
import * as React from 'react';

import { CommonStyles } from '../../commonInfra/styles/CommonStyles';
import { sanitize } from '../../commonInfra/utils/Sanitize';
import { HiddenUserStyles } from '../../spacesPlatform/components/spaceComponents/SpacesStyles';

import { ContentSuggestionExtraIconSize, ContentSuggestionIconSize } from '../common/Constants';
import { setupUIBindingForReactFunctionalComponent } from '../common/UIBindingForReact';
import { ContentDefinitions } from '../models/ContentDefinitions';
import { ContentSuggestionViewModel } from '../viewModels/ContentSuggestionViewModel';
import { EmptyStateContentSuggestionViewModel } from '../viewModels/EmptyStateContentSuggestionViewModel';
import { DraftTag } from './DraftTag';
import { activeSectionItem, alignCenter, contentSuggestionExtraIcons, contentSuggestionSectionItem, contentSuggestionSectionItemContent, contentSuggestionSectionItemIcon, section, sectionItem, sectionItemAutoFitContent, sectionItemFixedContent, sectionItemHorizontalLayout, useColorForHighlight } from './OmniboxV2Styles';
import { ContentSuggestionExtraIconType } from '../models/Types';
import { FeatureFlag, FeatureFlagService } from '../services/FeatureFlagService';

export const ContentSuggestionTestId = 'ContentSuggestion';

export function ContentSuggestion(props: { viewModel: ContentSuggestionViewModel }): JSX.Element {
    const viewModel = props.viewModel;
    setupUIBindingForReactFunctionalComponent(viewModel);

    return renderContentSuggestionItems(viewModel);
}

export function renderContentSuggestionItems(viewModel: ContentSuggestionViewModel | EmptyStateContentSuggestionViewModel): JSX.Element {
    return (!viewModel.contentSuggestionItems || viewModel.contentSuggestionItems.length > 0) ? <div data-tb-test-id={ContentSuggestionTestId} className={[!viewModel.displayedInEmptyStateUI && section, !viewModel.displayedInEmptyStateUI && 'tb-quicklinks-section-divider', (viewModel.omniboxViewModel.shouldUseColorHighlight() ? useColorForHighlight : null)].join(' ')}>
        {
            viewModel.contentSuggestionItems ? viewModel.contentSuggestionItems.map((item, index) => {
                const contentDefinition = ContentDefinitions[item.contentType];

                return <a id={item.getIdentifier()} key={index} href={item.url} role='option' aria-selected={item === viewModel.currentKeyboardNavigableItem}
                    onClick={(event) => { event.preventDefault(); event.stopPropagation(); viewModel.onItemClicked(item, index); }}
                    className={[sectionItem, sectionItemHorizontalLayout, contentSuggestionSectionItem, (item === viewModel.currentKeyboardNavigableItem ? activeSectionItem : '')].join(' ')}>
                    <div className={[sectionItemFixedContent, contentSuggestionSectionItemIcon].join(' ')}>
                        {item.icon({size: ContentSuggestionIconSize})}
                    </div>
                    <div className={[sectionItemAutoFitContent, contentSuggestionSectionItemContent].join(' ')}>
                        <div className={sectionItemHorizontalLayout}>
                            <div className={sectionItemAutoFitContent} style={{ position: 'relative', height: 21 }}>
                                <div className={sectionItemHorizontalLayout} style={{ position: 'absolute', width: '100%' }}>
                                    {item.hasDraft ?
                                        <>
                                            <div style={{ ...CommonStyles.Layout.TextEllipsis, maxWidth: 'calc(100% - 80px)' }} dangerouslySetInnerHTML={{
                                                __html:
                                                    sanitize(item.titleHtml)
                                            }}></div>
                                            <div className={sectionItemAutoFitContent}><DraftTag /></div>
                                        </> : <div style={{ ...CommonStyles.Layout.TextEllipsis }} dangerouslySetInnerHTML={{
                                            __html:
                                                sanitize(item.titleHtml)
                                        }}></div>
                                    }
                                </div>
                            </div>
                            <div className={sectionItemFixedContent}>
                                {item.supportingData}
                            </div>
                        </div>
                        <div className={contentSuggestionExtraIcons}>
                            {
                                item.extraIcons?.map(extraIcon => {
                                    let icon: React.ReactSVGElement;

                                    switch (extraIcon) {
                                        case ContentSuggestionExtraIconType.certified:
                                            icon = CertifiedBaseIcon({size: ContentSuggestionExtraIconSize});
                                            break;
                                        case ContentSuggestionExtraIconType.warningDataQualityAlert:
                                            icon = RESERVEDDatasourceCanInfoIcon({size: ContentSuggestionExtraIconSize, color: '#05628a'})
                                            break;
                                        case ContentSuggestionExtraIconType.severeDataQualityAlert:
                                            icon = RESERVEDDatasourceCanWarningIcon({size: ContentSuggestionExtraIconSize, color: '#ac4b02'})
                                            break;
                                        default:
                                            break;
                                    }

                                    return <>
                                                <span>{icon}</span>
                                                <span>&nbsp;&bull;&nbsp;</span>
                                           </>
                                })
                            }
                            <span data-tb-test-id='omnibox-suggestion-type'>
                                {contentDefinition.getName(item.data)}
                            </span>
                            {item.ownerHtml ? <span>&nbsp;&bull;&nbsp;</span> : null}
                            <span className={item.hiddenOwner ? HiddenUserStyles.HiddenUserClassName : ''} dangerouslySetInnerHTML={{
                                __html:
                                    sanitize(item.ownerHtml)
                            }}></span>
                            {FeatureFlagService.instance.getValue(FeatureFlag.dataCloudInCatalogMVPEnabled) &&
                                item.data && item.data.content.category && item.data.content.category!==''? 
                                <>
                                    <span>&nbsp;&bull;&nbsp;</span> 
                                    <span>{item.data.content.category}</span>
                                </>
                                : null
                            }
                        </div>
                    </div>
                </a>;
            })
                : <div className={alignCenter} style={{ height: '134px' }}>
                    <ActivitySpinnerWidget
                        spinnerSize={ActivitySpinnerSize.Small}
                        testId={'omnibox-pending'}
                    />
                </div>
        }
    </div>
        : null;
}